import { loadProgress } from '../shared/progress';
import { masteryPercent } from '../shared/student-kp';
import { dueKPsToday, totalDueCount } from './spaced-review';
import { buildRemediationMission } from './remediation';
import { getUiSoundPlayer } from './ui-sounds';
import { factionForSubject } from '../shared/factions';
import { getKPLabel, getChapterForKP, CURRICULUM } from './curriculum';
import { renderMasteryBox } from './components/MasteryBox';
import { renderDailyReviewBanner } from './components/DailyReviewBanner';
import { checkChapterMastered } from './mastery/chapterMasterDetector';
import { KP_TO_CHAPTER } from './mastery/chapter-kp-map';
import { ensureAcademicTheme } from './ui/theme';
import { mountBackButton } from './nav/back-button';
import type { Subject, StudentKP, PlayerProgress } from '../shared/types';

/**
 * Mastery W4 — hook called by quiz runners after `updateKPAfterAnswer` +
 * `saveProgress`. We look up the chapter the updated KP belongs to and ask
 * the detector whether the chapter has just been mastered. The detector
 * handles the first-time gate + CustomEvent dispatch internally; this
 * wrapper is intentionally side-effect-thin so it's safe to call from
 * any answer-flow site without coupling.
 *
 * Returns the chapterId that was just checked (regardless of whether the
 * detector fired) so callers can log / debug. Returns null when the kp
 * is not mapped to any chapter (e.g. archive-only KPs).
 *
 * Pure-fn-friendly: passing `null` progress is a no-op.
 */
export function notifyKPUpdated(
  kpId: string,
  progress: PlayerProgress | null,
): string | null {
  if (!progress) return null;
  const chapterId = KP_TO_CHAPTER[kpId];
  if (!chapterId) return null;
  try {
    checkChapterMastered(chapterId, progress);
  } catch {
    // Never let the detector break the answer flow.
  }
  return chapterId;
}

/**
 * Module-load listener: lets any layer dispatch a window-level
 * `'kp:updated'` event with `{ detail: { kpId, progress? } }` and we
 * call the detector. Falls back to `loadProgress()` when caller omits
 * progress. Idempotent — multiple module imports share the same flag.
 */
let _kpUpdateListenerAttached = false;
function attachKpUpdateListener(): void {
  if (_kpUpdateListenerAttached) return;
  if (typeof window === 'undefined') return;
  try {
    window.addEventListener('kp:updated', (raw: Event) => {
      const ev = raw as CustomEvent<{ kpId?: string; progress?: PlayerProgress }>;
      const kpId = ev.detail?.kpId;
      if (!kpId) return;
      const progress = ev.detail?.progress ?? (() => {
        try { return loadProgress(); } catch { return null; }
      })();
      notifyKPUpdated(kpId, progress);
    });
    _kpUpdateListenerAttached = true;
  } catch { /* headless */ }
}
attachKpUpdateListener();

export interface AcademyActions {
  /** Called when a subject card is tapped — navigate to that subject's chapter list */
  onSelectSubject: (subject: Subject) => void;
  onSelectExam: () => void;
  onSelectRemediation: () => void;
  onSelectPrintable: () => void;
  onBack: () => void;
  /**
   * Called when "章節練習卷" is tapped from a subject card.
   * Launches paper-mode chapter quiz for the given subject.
   */
  onSelectChapterQuiz?: (subject: Subject) => void;
  /** When true, subject cards show quantitative progress data (for parent viewing). */
  parentMode?: boolean;
}

const SUBJECTS: { id: Subject; label: string; emoji: string }[] = [
  { id: 'math',    label: '數學', emoji: '⚡' },
  { id: 'chinese', label: '國語', emoji: '🔮' },
  { id: 'english', label: '英語', emoji: '🤖' },
  { id: 'nature',  label: '自然', emoji: '🌀' },
  { id: 'social',  label: '社會', emoji: '🏰' },
];

/**
 * 老闆 2026-05-21 (early) complaint「學科大部分都不能點進去」 led to a
 * temporary placeholder for english / nature / social while their KPs were
 * unwired. MOONSHOT (#25) + Lesson wiring sprint (#41 Phase A + #42 Phase B)
 * shipped 109/109 KPs wired,所以 placeholder 已過時。empty array = 5 科全
 * 正常 route 進 chapter-list,不再 intercept。
 */
const COMING_SOON_SUBJECTS: Subject[] = [];

/** Internal: render the 即將推出 placeholder over the academy root. Exported for tests. */
export function renderComingSoonPlaceholder(
  container: HTMLElement,
  subject: Subject,
  onBack: () => void,
): void {
  const subjectMeta = SUBJECTS.find(s => s.id === subject);
  const emoji = subjectMeta?.emoji ?? '✨';
  const label = subjectMeta?.label ?? subject;
  const overlay = document.createElement('div');
  overlay.className = 'coming-soon-placeholder';
  overlay.innerHTML = `
    <h1>${emoji} ${label}</h1>
    <p style="font-size:18px;margin:18px 0;">即將推出</p>
    <p style="color:#888;font-size:14px;max-width:480px;line-height:1.6;">
      目前先用 ⚡ 數學 / 🔮 國語 修煉。${label}的關卡正在製作中,敬請期待!
    </p>
    <button class="primary-btn" id="cs-back" style="margin-top:24px;">返回</button>
  `;
  container.appendChild(overlay);
  overlay.querySelector('#cs-back')!.addEventListener('click', () => {
    overlay.remove();
    onBack();
  });
}

/**
 * 🧒 小明 2026-05-21 01:14 leftover fix: subject card used to show the literal
 * string 「今天的章節」 with no chapter resolution. Now we derive a real chapter:
 *  1. Most-recently-reviewed studentKP for this subject → its chapter title
 *  2. Fallback: chapter 1 of that subject in CURRICULUM (「等你來」 prefix)
 *  3. Fallback fallback: 「點擊開始」 (no chapters at all — shouldn't happen)
 */
export function getCurrentChapterLabel(subject: Subject, studentKPs: StudentKP[] | undefined): string {
  const reviewedForSubject = (studentKPs ?? [])
    .filter(kp => kp.subject === subject && kp.lastReviewedISO && kp.totalAttempts > 0)
    .sort((a, b) => (b.lastReviewedISO > a.lastReviewedISO ? 1 : -1));
  if (reviewedForSubject.length > 0) {
    const chapter = getChapterForKP(reviewedForSubject[0].kpId);
    if (chapter) return `第${chapter.number}章 ${chapter.title}`;
  }
  // Fallback: first chapter of subject
  const subjectCurriculum = CURRICULUM.find(s => s.subject === subject);
  if (subjectCurriculum && subjectCurriculum.chapters.length > 0) {
    const first = subjectCurriculum.chapters[0];
    return `等你來 · 第${first.number}章 ${first.title}`;
  }
  return '點擊開始';
}

export function renderAcademy(container: HTMLElement, actions: AcademyActions): void {
  // 學術風格設計系統(boss 2026-06-08;見 wiki page-470f59c0 / ui.md)。導覽 / 版面
  // 層改成教科書 / 講義紙感:紙底、serif 標題、單一學術綠重點色、無遊戲彩光(金黃 /
  // 門派色 / 夜空紫)。只重構導覽層 —— 答題引擎 / lesson 內容不在本檔範圍。
  ensureAcademicTheme();

  const isParentMode = actions.parentMode === true;

  // `.academy` 保留:既有測試以 `.academy` 斷言頁面存在 / 換頁移除;`ef-page` 套上
  // 紙底頁殼外觀(theme.ts 注入晚於 styles.css,覆蓋舊夜空 `.academy` 樣式)。
  const root = document.createElement('div');
  root.className = 'academy ef-page';

  // 紙感內容區(收窄置中,維持講義式閱讀寬度)。
  const inner = document.createElement('div');
  inner.className = 'ef-page__inner';
  root.appendChild(inner);

  // 標題列:serif 主標 + 細底線(`.ef-page__header` / `.ef-title`)。
  const header = document.createElement('header');
  header.className = 'ef-page__header';
  const heading = document.createElement('div');
  heading.className = 'ef-page__heading';
  const h1 = document.createElement('h1');
  h1.className = 'ef-title';
  h1.textContent = isParentMode ? '👪 修煉學院 (家長檢視)' : '修煉學院';
  heading.appendChild(h1);
  if (isParentMode) {
    const hint = document.createElement('p');
    // class 保留供既有測試斷言;外觀改用學術灰副標(去夜空青)。
    hint.className = 'parent-mode-hint ef-subtitle';
    hint.textContent = '顯示孩子各科熟練度';
    heading.appendChild(hint);
  }
  header.appendChild(heading);
  inner.appendChild(header);

  // 內容主體（mastery / 複習 / 學科 / 練習模式 / 進階挑戰 依序塞進這裡）。
  const main = document.createElement('div');
  main.className = 'academy-main ef-page__body';
  main.style.cssText = 'display:flex;flex-direction:column;gap:20px;';
  main.innerHTML = `
    <div id="subject-row" class="academy-subject-grid"></div>
    <section class="academy-section">
      <h2 class="ef-section-title">練習模式</h2>
      <div class="card-row academy-card-row" id="practice-mode-row">
        <div class="action-card ef-card" id="card-chapter-quiz">
          <div class="icon ef-card__icon" aria-hidden="true">📋</div>
          <div class="label ef-card__label">章節練習卷</div>
          <div class="sub ef-card__sub">整頁卷子，10 題計時</div>
        </div>
        <div class="action-card ef-card" id="card-flash-practice">
          <div class="icon ef-card__icon" aria-hidden="true">⚡</div>
          <div class="label ef-card__label">閃電練習</div>
          <div class="sub ef-card__sub">5 題快閃暖身</div>
        </div>
      </div>
    </section>
    <section class="academy-section">
      <h2 class="ef-section-title">進階挑戰</h2>
      <div class="card-row academy-card-row">
        <div class="action-card ef-card" id="card-exam">
          <div class="icon ef-card__icon" aria-hidden="true">📝</div>
          <div class="label ef-card__label">期末模擬考</div>
          <div class="sub ef-card__sub">10-20 題隨機抽樣</div>
        </div>
        <div class="action-card ef-card" id="card-printable">
          <div class="icon ef-card__icon" aria-hidden="true">📄</div>
          <div class="label ef-card__label">列印練習卷</div>
          <div class="sub ef-card__sub">印出來、寫完掃描</div>
        </div>
      </div>
    </section>
  `;
  inner.appendChild(main);

  // 統一 ‹ 返回:左上一致返回鈕 + 註冊集中式返回堆疊(左緣滑動共用)。換頁前 unmount。
  // boss 2026-06-14「回上一頁方式要統一」:只留這顆 ‹ 返回,移除舊的底部「回主畫面」鈕,
  // 不再一頁兩個返回鈕。
  const back = (): void => { offBack(); root.remove(); actions.onBack(); };
  const offBack = mountBackButton(inner, { onBack: back });

  // 學術風格的卡片網格 / 區塊微調(只本畫面 scope,不污染全域 styles.css)。
  ensureAcademyLayoutStyle();

  container.appendChild(root);

  // Mastery W3 — Daily Review banner + Mastery box ladder.
  // Both belong to the academy nav layer (boss memory feedback_learn_pro_mode):
  // OK to show retention banner / colored boxes here; quiz screen stays pro.
  // onStart callback is a no-op for now — DailyReviewBanner already dispatches
  // 'mastery:review:start' on window with the queue; W4 will wire the listener.
  // 留存元件（DailyReviewBanner / MasteryBox)為獨立 component,內部樣式自管,
  // 比照 learn-portal 的 renderMyProgressCard / StreakPill —— 本批刻意不動,只放進
  // 新紙感殼層(`main` body 最上方)。插入點由 root 改為 main(root 首子現為 inner)。
  const masteryWrap = document.createElement('div');
  masteryWrap.className = 'mastery-w3-wrap';
  masteryWrap.style.cssText = 'display:flex;flex-direction:column;gap:8px;';
  const progressSnapshot = loadProgress();
  const studentKPs = progressSnapshot?.studentKPs ?? [];
  renderDailyReviewBanner(masteryWrap, studentKPs, Date.now());
  renderMasteryBox(masteryWrap, studentKPs);
  main.insertBefore(masteryWrap, main.firstChild);

  // Remediation mission banner — insert as very first child if weak KPs exist
  // Game-world language only: NEVER shows 弱點/補強/錯題 to the kid
  const progress = progressSnapshot;
  if (progress) {
    buildRemediationMission(progress).then(mission => {
      if (!mission || !mission.phases) return;
      const banner = document.createElement('div');
      banner.className = 'remediation-banner';
      banner.innerHTML = `
        <div class="rem-title">${escapeHtml(mission.missionTitle)}</div>
        <button class="primary-btn" id="rem-go">前往支援</button>
      `;
      banner.querySelector('#rem-go')!.addEventListener('click', () => {
        root.remove();
        (window as unknown as { __pendingMission?: typeof mission }).__pendingMission = mission;
        actions.onSelectRemediation();
      });
      main.insertBefore(banner, main.firstChild);
    });
  }

  // "今日複習" section — insert ABOVE the subject row if there are due KPs
  const due = progress ? dueKPsToday(progress) : [];
  const totalDue = progress ? totalDueCount(progress) : 0;

  if (due.length > 0) {
    const reviewSection = document.createElement('section');
    reviewSection.className = 'academy-section academy-due';
    reviewSection.innerHTML = `<h2 class="ef-section-title">今日該複習</h2>`;
    const dueGrid = document.createElement('div');
    dueGrid.className = 'academy-subject-grid';
    reviewSection.appendChild(dueGrid);
    for (const kp of due) {
      const card = document.createElement('div');
      // library-card 保留供既有共用樣式 / 慣例;ef-card 套紙片外觀,去門派色 left-border。
      card.className = 'library-card ef-card';
      card.innerHTML = `
        <div class="lc-title ef-card__label">${escapeHtml(getKPLabel(kp.kpId))}</div>
        <div class="lc-meta ef-card__sub">熟練度 ${masteryPercent(kp)}%</div>
      `;
      // Click: launch the appropriate lesson for that KP's subject
      card.addEventListener('click', () => {
        getUiSoundPlayer().play('ui-click', 0.3);
        root.remove();
        actions.onSelectSubject(kp.subject);
      });
      dueGrid.appendChild(card);
    }
    if (totalDue > due.length) {
      const more = document.createElement('p');
      more.className = 'ef-subtitle';
      more.style.cssText = 'margin:0;';
      more.textContent = `+${totalDue - due.length} 個待複習(優先做最脆弱的)`;
      reviewSection.appendChild(more);
    }
    // Insert before subject row (which is the first child of main after retention banners)
    const subjectRow = main.querySelector('#subject-row')!;
    main.insertBefore(reviewSection, subjectRow);
  }
  const row = root.querySelector('#subject-row')!;
  for (const s of SUBJECTS) {
    const faction = factionForSubject(s.id);
    const card = document.createElement('div');
    // library-card 保留供既有測試 / 共用樣式;ef-card 套紙片外觀,門派色不再 leak 進
    // 學術導覽(data-faction 仍保留供資料層 / 測試,僅不再驅動視覺色)。
    card.className = 'library-card ef-card';
    card.dataset.faction = faction;

    if (isParentMode) {
      // Compute subject-level mastery: average of all reviewed KPs for this subject
      const subjectKPs = (progress?.studentKPs ?? []).filter(kp => kp.subject === s.id && kp.totalAttempts > 0);
      const avgMastery = subjectKPs.length > 0
        ? Math.round(subjectKPs.reduce((sum, kp) => sum + masteryPercent(kp), 0) / subjectKPs.length)
        : 0;
      const kpCount = subjectKPs.length;
      card.innerHTML = `
        <div class="lc-title ef-card__label">${s.emoji} ${s.label}</div>
        <div class="lc-meta ef-card__sub">${kpCount > 0 ? `${kpCount} 個單元 • 熟練度 ${avgMastery}%` : '尚無練習記錄'}</div>
        ${kpCount > 0 ? `<div class="parent-progress-bar" style="margin-top:8px;height:6px;background:var(--ef-line);border-radius:999px;overflow:hidden;"><div style="width:${avgMastery}%;height:100%;background:var(--ef-green);border-radius:999px;"></div></div>` : ''}
      `;
    } else {
      // 🧒 小明 2026-05-21 01:14 leftover fix: show real chapter from progress
      // (replaces placeholder「今天的章節」 — kid wants to know WHICH chapter).
      const chapterLabel = getCurrentChapterLabel(s.id, progress?.studentKPs);
      card.innerHTML = `
        <div class="lc-title ef-card__label">${s.emoji} ${s.label}</div>
        <div class="lc-meta ef-card__sub">${escapeHtml(chapterLabel)}</div>
      `;
    }

    card.addEventListener('click', () => {
      getUiSoundPlayer().play('ui-click', 0.3);
      // 老闆 2026-05-21 fix: english / nature / social → show 即將推出 placeholder
      // instead of routing into a chapter flow whose KPs silently no-op.
      if (COMING_SOON_SUBJECTS.includes(s.id)) {
        root.remove();
        renderComingSoonPlaceholder(container, s.id, () => {
          // Re-enter academy on 返回 so the kid isn't stranded.
          renderAcademy(container, actions);
        });
        return;
      }
      root.remove();
      actions.onSelectSubject(s.id);
    });
    row.appendChild(card);
  }
  // Chapter quiz — shows subject picker if no handler; defaults to math for quick access
  root.querySelector('#card-chapter-quiz')!.addEventListener('click', () => {
    if (actions.onSelectChapterQuiz) {
      root.remove();
      // Default to math chapter 1 as entry point; subject selection handled by caller
      actions.onSelectChapterQuiz('math');
    } else {
      // Fallback: navigate to subject selection
      root.remove();
      actions.onSelectSubject('math');
    }
  });

  // Flash practice (閃電練習) — routes to existing exam flow
  root.querySelector('#card-flash-practice')!.addEventListener('click', () => {
    root.remove();
    actions.onSelectExam();
  });

  root.querySelector('#card-exam')!.addEventListener('click', () => { root.remove(); actions.onSelectExam(); });
  root.querySelector('#card-printable')!.addEventListener('click', () => { root.remove(); actions.onSelectPrintable(); });
  // #back-academy 的 click 已在 academicButton onClick 內接好,這裡不再重複掛載。
}

function escapeHtml(s: string): string {
  const d = document.createElement('div'); d.textContent = s; return d.innerHTML;
}

/** 本畫面 scope 的學術風格版面樣式 id —— 冪等守衛 / 測試用。 */
export const ACADEMY_LAYOUT_STYLE_ID = 'ef-academy-layout';

/**
 * 冪等注入「課程」導覽層的版面微調(學術風格)。只覆蓋本畫面用到的 class,不污染
 * 全域 styles.css(boss 範圍限制)。把舊夜空 `.academy` / 金黃標題 / 門派色 left-border
 * 覆蓋成教科書 / 講義紙感:紙片卡、學術綠 serif 區塊標題、卡片網格。
 *
 * 注入晚於 styles.css(runtime),故同名選擇器以此為準。
 */
function ensureAcademyLayoutStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(ACADEMY_LAYOUT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = ACADEMY_LAYOUT_STYLE_ID;
  style.textContent = ACADEMY_LAYOUT_CSS;
  document.head.appendChild(style);
}

const ACADEMY_LAYOUT_CSS = `
/* 「課程」導覽層 —— 學術風格版面（覆蓋舊夜空 .academy / 金黃 / 門派色)。 */
.academy.ef-page { gap: 0; }

/* 區塊小標:serif + 學術綠細左槓(取代舊金黃 h2)。 */
.academy .ef-section-title {
  margin: 0 0 12px;
  font-family: var(--ef-serif);
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.5px;
  color: var(--ef-ink);
  padding-left: 12px;
  border-left: 4px solid var(--ef-green);
  line-height: 1.3;
}
.academy .academy-section { margin: 0; }

/* 學科 / 複習卡 → 自適應紙片網格。 */
.academy .academy-subject-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
}

/* 練習模式 / 進階挑戰卡列 → 同一套自適應紙片網格。 */
.academy .academy-card-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
}

/* 學科卡:套 .ef-card 紙片外觀,清掉舊 .library-card 的門派色左槓 / 漸層 / 位移。 */
.academy .library-card.ef-card {
  background: var(--ef-paper-bright);
  border: 1px solid var(--ef-line);
  border-left: 1px solid var(--ef-line);
  border-radius: var(--ef-radius);
  padding: 18px;
  margin: 0;
}
.academy .library-card.ef-card:hover { transform: translateY(-2px); }
.academy .library-card .lc-title.ef-card__label { color: var(--ef-ink); }
.academy .library-card .lc-meta.ef-card__sub { color: var(--ef-sub); margin-top: 4px; }

/* 練習 / 挑戰 action-card → 紙片卡(置中 icon + serif 標題 + 灰副標)。 */
.academy .action-card.ef-card {
  align-items: flex-start;
  text-align: left;
  gap: 6px;
}
.academy .action-card .label.ef-card__label { color: var(--ef-ink); }
.academy .action-card .sub.ef-card__sub { color: var(--ef-sub); }

/* 補強任務 banner → 便利貼黃底 + 學術綠 CTA(取代舊夜空 + 金黃)。 */
.academy .remediation-banner {
  background: var(--ef-note);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius);
  box-shadow: none;
  padding: 16px 18px;
  margin: 0 0 4px;
}
.academy .remediation-banner .rem-title { color: var(--ef-ink); font-weight: 600; }
.academy .remediation-banner .primary-btn {
  background: var(--ef-green);
  border: 1px solid var(--ef-green);
  color: #fff;
  border-radius: var(--ef-radius-pill);
  padding: 9px 20px;
  font-weight: 700;
  cursor: pointer;
}
.academy .remediation-banner .primary-btn:hover { background: var(--ef-green-dark); }

/* 「即將推出」placeholder → 紙感(門派全 wired 後 COMING_SOON 已空,留樣式備用)。 */
.coming-soon-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 48px 24px;
  background: radial-gradient(120% 80% at 50% 0%, #faf8f1 0%, var(--ef-paper) 60%);
  min-height: 100%;
  font-family: var(--ef-sans);
  color: var(--ef-ink-soft);
}
.coming-soon-placeholder h1 {
  font-family: var(--ef-serif);
  color: var(--ef-ink);
  margin: 0;
}
.coming-soon-placeholder .primary-btn {
  background: var(--ef-green);
  border: 1px solid var(--ef-green);
  color: #fff;
  border-radius: var(--ef-radius-pill);
  padding: 11px 28px;
  font-weight: 700;
  cursor: pointer;
}
.coming-soon-placeholder .primary-btn:hover { background: var(--ef-green-dark); }

@media (min-width: 640px) {
  .academy .ef-section-title { font-size: 20px; }
}
`;
